'use client';

import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {Button} from '@udemy-v2/react-core-components';
import {PlayOverlay} from '@udemy-v2/react-structure-components';

import {ConsentGate} from './consent-gate.react-component';
import {hasExternalVideoConsent, setExternalVideoConsent} from './external-video-consent';

import {useYouTubeThumbnail} from '@/features/instructor-profile/hooks/use-youtube-thumbnail';
import {parseYouTubeId, toEmbedUrl} from '@/features/instructor-profile/lib/parse-youtube-id';
import type {InstructorProfile} from '@/features/instructor-profile/lib/types';

import styles from './biography.module.scss';

/** Must match collapsed max-block-size in biography.module.scss */
const BIO_COLLAPSED_MAX_BLOCK_SIZE_PX = 260;
/** Must match the max-block-size transition duration in biography.module.scss */
const BIO_COLLAPSE_TRANSITION_MS = 500;

interface BiographyProps {
    /**
     * The instructor profile to render. Presentational only — this component
     * never fetches; the public profile page and (later) the instructor preview
     * tab both pass already-resolved data here.
     */
    profile: InstructorProfile;
    /**
     * When true, clicking play shows a consent gate instead of immediately
     * loading the YouTube iframe. Pass true on the public profile page,
     * false (or omit) in preview contexts where consent is not required.
     */
    showConsentGate?: boolean;
}

export function Biography({profile, showConsentGate = false}: BiographyProps) {
    const {
        display_name: displayName,
        description,
        video_intro_url: videoIntroUrl,
        url_facebook: facebookProfile,
        url_instagram: instagramProfile,
        url_linkedin: linkedinProfile,
        url_tiktok: tiktokProfile,
        url_youtube: youtubeProfile,
        url_twitter: twitterProfile,
        url_personal_website: urlPersonalWebsite,
    } = profile;

    const hasContent = !!description || !!videoIntroUrl;
    if (!hasContent) return null;

    const layoutClassName = videoIntroUrl
        ? styles['layout']
        : `${styles['layout']} ${styles['layout--no-video']}`;

    return (
        <section className={styles['biography']} aria-labelledby="bio-heading">
            <div className={layoutClassName}>
                <div className={styles['header-group']}>
                    <BioHeading name={displayName || ''} />
                </div>
                <SocialLinks
                    facebookProfile={facebookProfile}
                    instagramProfile={instagramProfile}
                    linkedinProfile={linkedinProfile}
                    tiktokProfile={tiktokProfile}
                    youtubeProfile={youtubeProfile}
                    twitterProfile={twitterProfile}
                    urlPersonalWebsite={urlPersonalWebsite}
                />
                {videoIntroUrl && (
                    <div className={styles['video-column']}>
                        <VideoPlayer
                            videoIntroUrl={videoIntroUrl}
                            showConsentGate={showConsentGate}
                        />
                    </div>
                )}
                {description && <BioProse description={description} />}
            </div>
        </section>
    );
}

function BioHeading({name}: {name: string}) {
    const {gettext, interpolate} = useI18n();
    return (
        <h2 id="bio-heading" className={styles['heading']}>
            {interpolate(gettext("Hi, I'm %(name)s"), {name}, true)}
        </h2>
    );
}

interface SocialLinksProps {
    facebookProfile?: string;
    instagramProfile?: string;
    linkedinProfile?: string;
    tiktokProfile?: string;
    youtubeProfile?: string;
    twitterProfile?: string;
    urlPersonalWebsite?: string;
}

function SocialLinks({
    facebookProfile,
    instagramProfile,
    linkedinProfile,
    tiktokProfile,
    youtubeProfile,
    twitterProfile,
    urlPersonalWebsite,
}: SocialLinksProps) {
    const {gettext} = useI18n();

    const links = [
        {url: facebookProfile, glyph: 'facebook' as const, label: gettext('Facebook profile')},
        {url: instagramProfile, glyph: 'instagram' as const, label: gettext('Instagram profile')},
        {url: linkedinProfile, glyph: 'linkedin' as const, label: gettext('LinkedIn profile')},
        {url: tiktokProfile, glyph: 'tiktok' as const, label: gettext('TikTok profile')},
        {url: youtubeProfile, glyph: 'youtube' as const, label: gettext('YouTube channel')},
        {url: twitterProfile, glyph: 'twitter' as const, label: gettext('Twitter profile')},
        {url: urlPersonalWebsite, glyph: 'language' as const, label: gettext('Personal website')},
    ].filter(({url}) => !!url);

    if (links.length === 0) return null;

    return (
        <ul className={styles['social-links']}>
            {links.map(({url, glyph, label}) => (
                <li key={glyph} className={styles['social-item']}>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className={styles['social-link']}
                    >
                        <Icon glyph={glyph} label={false} size="medium" color="inherit" />
                    </a>
                </li>
            ))}
        </ul>
    );
}

function BioProse({description}: {description: string}) {
    const {gettext} = useI18n();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const [maxBlockSize, setMaxBlockSize] = useState(BIO_COLLAPSED_MAX_BLOCK_SIZE_PX);
    const [collapsedLines, setCollapsedLines] = useState(0);
    const [isClamped, setIsClamped] = useState(true);
    const bodyRef = useRef<HTMLDivElement>(null);
    const clampTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        return () => clearTimeout(clampTimeoutRef.current);
    }, []);

    // Content is rich HTML (paragraphs, list-like blocks with their own margins),
    // so a pixel height cutoff can slice through the middle of a line whenever a
    // paragraph boundary doesn't land on a clean multiple of the line height.
    // Clamping by line count instead guarantees the cut always falls on a full line.
    const getLineHeightPx = useCallback((el: HTMLDivElement) => {
        return parseFloat(getComputedStyle(el).lineHeight) || 0;
    }, []);

    const syncBioLayout = useCallback(() => {
        const el = bodyRef.current;
        if (!el) return;

        const lineHeightPx = getLineHeightPx(el);
        const lines = lineHeightPx
            ? Math.max(1, Math.floor(BIO_COLLAPSED_MAX_BLOCK_SIZE_PX / lineHeightPx))
            : 0;
        setCollapsedLines(lines);

        const fullHeight = el.scrollHeight;
        const collapsedBlockSize =
            lines && lineHeightPx ? lines * lineHeightPx : BIO_COLLAPSED_MAX_BLOCK_SIZE_PX;
        setIsTruncated(fullHeight > collapsedBlockSize);
        setMaxBlockSize(isExpanded ? fullHeight : collapsedBlockSize);
    }, [isExpanded, getLineHeightPx]);

    useLayoutEffect(() => {
        // Measures rendered DOM (scrollHeight, line-height) to derive clamp state,
        // so this can't be computed during render — the documented exception for
        // useLayoutEffect + setState.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        syncBioLayout();
        window.addEventListener('resize', syncBioLayout);
        return () => window.removeEventListener('resize', syncBioLayout);
    }, [syncBioLayout, description]);

    const handleToggle = useCallback(() => {
        const el = bodyRef.current;
        if (!el) return;

        clearTimeout(clampTimeoutRef.current);

        if (isExpanded) {
            // Un-clamp first so overflow:hidden (not the unanimatable line-clamp)
            // drives the shrink — the transition can then land on a full line.
            // Re-apply the clamp (and its ellipsis) once the shrink finishes.
            setIsClamped(false);
            const lineHeightPx = getLineHeightPx(el);
            setMaxBlockSize(
                lineHeightPx ? collapsedLines * lineHeightPx : BIO_COLLAPSED_MAX_BLOCK_SIZE_PX,
            );
            setIsExpanded(false);
            clampTimeoutRef.current = setTimeout(
                () => setIsClamped(true),
                BIO_COLLAPSE_TRANSITION_MS,
            );
            return;
        }

        setIsClamped(false);
        setMaxBlockSize(el.scrollHeight);
        setIsExpanded(true);
    }, [isExpanded, collapsedLines, getLineHeightPx]);

    return (
        <div className={styles['bio-block']}>
            <div
                ref={bodyRef}
                className={styles['bio-body']}
                style={{
                    maxBlockSize: `${maxBlockSize}px`,
                    ...(!isExpanded && isClamped && collapsedLines > 0
                        ? {
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: collapsedLines,
                          }
                        : null),
                }}
                // description is HTML from the Udemy API (server-authored rich text)
                dangerouslySetInnerHTML={{__html: description}}
            />
            {(isTruncated || isExpanded) && (
                <div className={styles['show-more-row']}>
                    <Button
                        udStyle="ghost"
                        size="xsmall"
                        className={styles['show-more-button']}
                        onClick={handleToggle}
                        aria-expanded={isExpanded}
                    >
                        {isExpanded ? gettext('Show less') : gettext('Show more')}
                        <Icon
                            glyph={isExpanded ? 'collapse' : 'expand'}
                            label={false}
                            color="inherit"
                        />
                    </Button>
                </div>
            )}
        </div>
    );
}

interface VideoPlayerProps {
    videoIntroUrl: string;
    showConsentGate?: boolean;
}

function VideoPlayer({videoIntroUrl, showConsentGate = false}: VideoPlayerProps) {
    const {gettext} = useI18n();
    const [isPlaying, setIsPlaying] = useState(false);
    const [consentPending, setConsentPending] = useState(false);

    const videoId = parseYouTubeId(videoIntroUrl);
    const {
        src: thumbnailSrc,
        onError: onThumbnailError,
        onLoad: onThumbnailLoad,
    } = useYouTubeThumbnail(videoId);
    if (!videoId) return null;

    const handlePlayClick = () => {
        if (showConsentGate && !hasExternalVideoConsent()) {
            setConsentPending(true);
        } else {
            setIsPlaying(true);
        }
    };

    const handleConsentAccept = (rememberChoice: boolean) => {
        if (rememberChoice) {
            setExternalVideoConsent();
        }
        setConsentPending(false);
        setIsPlaying(true);
    };

    const handleConsentDismiss = () => {
        setConsentPending(false);
    };

    return (
        <div className={styles['video-wrapper']}>
            {isPlaying ? (
                <iframe
                    className={styles['video-iframe']}
                    src={`${toEmbedUrl(videoId)}?autoplay=1&controls=0&rel=0&modestbranding=1`}
                    title={gettext('Instructor introduction video')}
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            ) : (
                <>
                    <button
                        type="button"
                        className={styles['video-play-button']}
                        onClick={handlePlayClick}
                        aria-label={gettext('Play instructor introduction video')}
                    >
                        {thumbnailSrc ? (
                            /* eslint-disable-next-line @next/next/no-img-element */
                            <img
                                className={styles['video-thumbnail']}
                                src={thumbnailSrc}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                onError={onThumbnailError}
                                onLoad={onThumbnailLoad}
                            />
                        ) : (
                            <div className={styles['video-placeholder']} />
                        )}
                        <PlayOverlay size="xxlarge" />
                    </button>
                    {consentPending && (
                        <ConsentGate
                            onAccept={handleConsentAccept}
                            onDismiss={handleConsentDismiss}
                        />
                    )}
                </>
            )}
        </div>
    );
}
